import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Box, Typography, InputBase, CircularProgress } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { CreateFacility } from "../../hooks/useFacilityData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder, autoFocus }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

export const CreateSubjectModal = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [facilityName, setFacilityName] = useState("");

  const organisationId = props.customerid;

  const createFacilityMutation = CreateFacility({
    onSuccess: () => {
      enqueueSnackbar("Anläggning skapad.", { variant: "success" });
      props.closeCreateModal();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte skapa anläggning.", { variant: "error" });
    },
  });

  const handleCreate = () => {
    if (!facilityName.trim()) return;
    createFacilityMutation.mutate({
      organisation_id: parseInt(organisationId),
      facility_name: facilityName.trim(),
    });
  };

  const saving = createFacilityMutation.isLoading;
  const canSave = facilityName.trim().length > 0 && !saving;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Identitet</SectionLabel>
        <Box>
          <FieldLabel>Namn på anläggning</FieldLabel>
          <TextInput
            value={facilityName}
            onChange={(e) => setFacilityName(e.target.value)}
            placeholder="T.ex. Huvudkontor Stockholm"
            autoFocus
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => props.closeCreateModal()}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.25,
            background: "transparent",
            color: INK_MUTED,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              color: INK,
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
          Avbryt
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleCreate}
          disabled={!canSave}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.25,
            background: INK,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.4,
            transition: "all 0.25s ease",
            boxShadow: canSave ? "0 10px 24px -12px rgba(10,22,40,0.55)" : "none",
            "&:hover": canSave
              ? { background: "#1a2638", transform: "translateY(-1px)" }
              : {},
          }}
        >
          {saving ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          )}
          Skapa anläggning
        </Box>
      </Box>
    </Box>
  );
};
